import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FaArrowRight, FaCheckCircle, FaColumns, FaCode, FaLock } from 'react-icons/fa';
import { useLanguage } from '../i18n/LanguageContext';
import { trackLandingEvent, withStoreUtm } from '../services/analytics';

const STORE_URL = withStoreUtm('hero_primary');

const HeroSection = styled.section`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 2rem;
  min-height: calc(100svh - 180px);
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 2.4rem 1rem 2rem;
    min-height: auto;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.82fr) minmax(600px, 1.18fr);
  align-items: center;
  gap: 3rem;

  @media (max-width: 1040px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const HeroContent = styled.div`
  max-width: 560px;
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.42rem 0.7rem;
  border-radius: var(--radius-small);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: 0.86rem;
  font-weight: 600;
  border: 1px solid var(--color-border);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  margin-bottom: 1.2rem;

  svg {
    color: var(--color-accent);
  }
`;

const HeroTitle = styled.h1`
  color: var(--color-text-primary);
  font-size: clamp(2.6rem, 5vw, 5.1rem);
  line-height: 0.98;
  font-weight: 700;
  letter-spacing: 0;
  margin-bottom: 1.1rem;
`;

const HeroDescription = styled.p`
  color: var(--color-text-secondary);
  font-size: clamp(1.05rem, 1.8vw, 1.25rem);
  line-height: 1.65;
  max-width: 540px;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-top: 1.8rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.88rem 1.15rem;
  border-radius: var(--radius-small);
  background: var(--color-cta);
  color: #111827;
  text-decoration: none;
  font-weight: 700;
  box-shadow: 0 12px 28px rgba(245, 158, 11, 0.24);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

  &:hover {
    background: #fbbf24;
    transform: translateY(-1px);
    box-shadow: 0 16px 34px rgba(245, 158, 11, 0.3);
  }
`;

const SecondaryLink = styled.a`
  color: var(--color-accent);
  font-weight: 700;
  text-decoration: none;

  &:hover {
    color: var(--color-accent-dark);
  }
`;

const ProofList = styled.div`
  display: grid;
  gap: 0.7rem;
  margin-top: 2rem;
`;

const ProofItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: var(--color-text-secondary);
  font-weight: 600;

  svg {
    color: var(--color-accent);
    flex: 0 0 auto;
  }
`;

const ProductFrame = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-large);
  box-shadow: var(--shadow-medium);
  overflow: hidden;
`;

const ProductToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0.9rem;
  border-bottom: 1px solid var(--color-border);
  background: #ffffff;
`;

const ToolbarTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-weight: 700;
  color: var(--color-text-primary);

  span {
    width: 18px;
    height: 18px;
    border-radius: 5px;
    background: linear-gradient(135deg, var(--color-accent), #3b82f6);
  }
`;

const ToolbarControls = styled.div`
  display: flex;
  gap: 0.4rem;

  button {
    width: 28px;
    height: 24px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-small);
    background: var(--color-surface-alt);
  }
`;

const PreviewLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(210px, 0.74fr) minmax(0, 1.26fr);
  min-height: 450px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SheetMock = styled.div`
  padding: 0.9rem;
  border-right: 1px solid var(--color-border);
  background: #f8fafc;

  @media (max-width: 640px) {
    border-right: none;
    border-bottom: 1px solid var(--color-border);
  }
`;

const SheetGrid = styled.div`
  display: grid;
  grid-template-columns: 34px repeat(3, minmax(58px, 1fr));
  border: 1px solid var(--color-border);
  background: #ffffff;

  div {
    min-height: 36px;
    padding: 0.42rem;
    border-right: 1px solid var(--color-border);
    border-bottom: 1px solid var(--color-border);
    color: var(--color-text-muted);
    font-size: 0.68rem;
    line-height: 1.35;
    overflow: hidden;
    word-break: break-word;
  }

  .head {
    background: var(--color-surface-alt);
    color: var(--color-accent);
    font-weight: 700;
  }

  .active {
    color: var(--color-text-primary);
    outline: 2px solid var(--color-accent);
    outline-offset: -2px;
    background: #ffffff;
    font-weight: 700;
    max-height: 108px;
  }
`;

const PreviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FormatTabs = styled.div`
  display: flex;
  gap: 0.45rem;
  padding: 0.75rem 0.85rem;
  border-bottom: 1px solid var(--color-border);
  overflow-x: auto;
`;

const FormatTab = styled.button<{ $active: boolean }>`
  border: 1px solid ${props => props.$active ? 'var(--color-accent)' : 'var(--color-border)'};
  background: ${props => props.$active ? 'var(--color-accent-soft)' : '#ffffff'};
  color: ${props => props.$active ? 'var(--color-accent)' : 'var(--color-text-muted)'};
  border-radius: var(--radius-small);
  padding: 0.4rem 0.58rem;
  font-weight: 700;
  font-size: 0.78rem;
  cursor: pointer;
  white-space: nowrap;
`;

const RenderedPreview = styled.div`
  padding: 1rem;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 640px) {
    padding: 0.75rem;
  }
`;

const PreviewSurface = styled.div`
  width: 100%;
  height: 100%;
  min-height: 330px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-small);
  background: #ffffff;
  overflow: auto;
`;

const MarkdownSurface = styled(PreviewSurface)`
  padding: 1.05rem;
  color: var(--color-text-primary);
  font-size: 0.88rem;

  h3 {
    font-size: 1.05rem;
    margin-bottom: 0.7rem;
  }

  p,
  li {
    color: var(--color-text-secondary);
    line-height: 1.55;
  }

  ol {
    margin: 0.8rem 0 1rem 1.1rem;
  }

  strong {
    color: var(--color-text-primary);
  }
`;

const CodeSample = styled.pre`
  margin-top: 0.8rem;
  padding: 0.85rem;
  border-radius: var(--radius-small);
  background: #1f241d;
  color: #d6decb;
  font-size: 0.72rem;
  line-height: 1.55;
  overflow-x: auto;
`;

const JsonSurface = styled(PreviewSurface)`
  padding: 0.9rem;
  background: #20241f;
  color: #d6decb;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.78rem;
  line-height: 1.62;

  .key {
    color: #e5e7eb;
  }

  .string {
    color: #f59e0b;
  }

  .number {
    color: #67e8f9;
  }

  .indent-1 {
    padding-left: 1rem;
  }

  .indent-2 {
    padding-left: 2rem;
  }
`;

const LinkSurface = styled(PreviewSurface)`
  background: #050505;
  color: white;
  display: flex;
  flex-direction: column;
`;

const VideoTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0.85rem;
  font-weight: 700;
  font-size: 0.9rem;
`;

const VideoBody = styled.div`
  flex: 1;
  min-height: 180px;
  display: grid;
  place-items: center;
  background:
    radial-gradient(circle at 40% 45%, rgba(59, 130, 246, 0.35), transparent 24%),
    linear-gradient(135deg, #111827 0%, #242933 48%, #0f172a 100%);

  span {
    color: #60a5fa;
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: 0;
  }
`;

const VideoControls = styled.div`
  padding: 0.75rem 0.85rem;
  display: grid;
  gap: 0.58rem;

  .bar {
    height: 4px;
    border-radius: 999px;
    background: linear-gradient(90deg, #ef4444 0 18%, #94a3b8 18% 100%);
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #e5e7eb;
    font-size: 0.82rem;
  }
`;

const LatexSurface = styled(PreviewSurface)`
  padding: 1.05rem;
  color: var(--color-text-primary);
  font-size: 0.88rem;

  p,
  li {
    color: var(--color-text-secondary);
    line-height: 1.55;
  }

  ol {
    margin: 1rem 0 0 1.2rem;
  }
`;

const Formula = styled.div`
  margin: 0.8rem 0;
  padding: 0.8rem;
  border-radius: var(--radius-small);
  background: #f8fafc;
  border: 1px solid var(--color-border);
  color: #111827;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 1rem;
  line-height: 1.7;
  overflow-x: auto;
  white-space: nowrap;
`;

const InlineBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #ffffff;
  color: var(--color-accent);
  font-weight: 800;
`;

const UrlStrip = styled.div`
  padding: 0.55rem 0.85rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #cbd5e1;
  font-size: 0.7rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BelowFoldCue = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  color: var(--color-text-muted);
  font-size: 0.9rem;
`;

type SlideId = 'markdown' | 'json' | 'link' | 'latex';

type DemoSlide = {
  id: SlideId;
  label: string;
  column: string;
  rowLabel: string;
  cell: string;
  note: string;
};

const renderPreview = (id: SlideId, t: (key: string) => string) => {
  switch (id) {
    case 'markdown':
      return (
        <MarkdownSurface>
          <h3>{t('heroDemo.markdown.title')}</h3>
          <p>
            {t('heroDemo.markdown.description')}
          </p>
          <ol>
            <li><strong>{t('heroDemo.markdown.step1Label')}</strong> {t('heroDemo.markdown.step1Text')}</li>
            <li><strong>{t('heroDemo.markdown.step2Label')}</strong> {t('heroDemo.markdown.step2Text')}</li>
          </ol>
          <CodeSample>{`def solve(rows):
    total = 0
    for item in rows:
        total += item["score"]
    return total`}</CodeSample>
        </MarkdownSurface>
      );
    case 'json':
      return (
        <JsonSurface>
          <div>{'{'}</div>
          <div className="indent-1"><span className="key">{'"status"'}</span>: <span className="string">{'"ready"'}</span>,</div>
          <div className="indent-1"><span className="key">{'"owner"'}</span>: <span className="string">{'"ops"'}</span>,</div>
          <div className="indent-1"><span className="key">{'"metrics"'}</span>: {'{'}</div>
          <div className="indent-2"><span className="key">{'"rows"'}</span>: <span className="number">1280</span>,</div>
          <div className="indent-2"><span className="key">{'"passed"'}</span>: <span className="number">1246</span></div>
          <div className="indent-1">{'}'},</div>
          <div className="indent-1"><span className="key">{'"tags"'}</span>: [<span className="string">{'"qa"'}</span>, <span className="string">{'"release"'}</span>]</div>
          <div>{'}'}</div>
        </JsonSurface>
      );
    case 'link':
      return (
        <LinkSurface>
          <VideoTop>
            <span>{t('heroDemo.link.previewTitle')}</span>
            <InlineBadge>G</InlineBadge>
          </VideoTop>
          <UrlStrip>https://www.youtube.com/embed/demo-report</UrlStrip>
          <VideoBody>
            <span>Gemma</span>
          </VideoBody>
          <VideoControls>
            <div className="bar" />
            <div className="row">
              <span>0:04 / 4:20</span>
              <span>YouTube</span>
            </div>
          </VideoControls>
        </LinkSurface>
      );
    case 'latex':
      return (
        <LatexSurface>
          <p>
            {t('heroDemo.latex.description')}
          </p>
          <Formula>
            <span>Π</span><sub>k=0</sub><sup>12</sup>
            <span> (x - (1 + i)ω</span><sup>k</sup><span>) = (1 + i)</span><sup>13</sup><span> - 1</span>
          </Formula>
          <ol>
            <li><strong>{t('heroDemo.latex.step1Label')}</strong> x = ω<sup>k</sup>.</li>
            <li><strong>{t('heroDemo.latex.step2Label')}</strong> x<sup>13</sup> = 1.</li>
            <li><strong>{t('heroDemo.latex.step3Label')}</strong> {t('heroDemo.latex.step3Text')}</li>
          </ol>
        </LatexSurface>
      );
    default:
      return null;
  }
};

const Hero: React.FC = () => {
  const { t } = useLanguage();
  const [activeSlide, setActiveSlide] = useState(0);

  const slides = useMemo<DemoSlide[]>(() => [
    {
      id: 'markdown' as const,
      label: 'Markdown',
      column: 'response',
      rowLabel: t('heroDemo.markdown.rowLabel'),
      cell: t('heroDemo.markdown.cell'),
      note: t('heroDemo.markdown.note')
    },
    {
      id: 'json' as const,
      label: 'JSON',
      column: 'json_payload',
      rowLabel: t('heroDemo.json.rowLabel'),
      cell: '{"status":"ready","owner":"ops","metrics":{"rows":1280,"passed":1246},"tags":["qa","release"]}',
      note: t('heroDemo.json.note')
    },
    {
      id: 'link' as const,
      label: t('heroDemo.link.label'),
      column: 'embed_url',
      rowLabel: t('heroDemo.link.rowLabel'),
      cell: 'https://www.youtube.com/embed/demo-report',
      note: t('heroDemo.link.note')
    },
    {
      id: 'latex' as const,
      label: 'LaTeX',
      column: 'math_answer',
      rowLabel: t('heroDemo.latex.rowLabel'),
      cell: 'Π_{k=0}^{12}(x-(1+i)ω^k)=(1+i)^{13}-1',
      note: t('heroDemo.latex.note')
    }
  ], [t]);

  const activeSlideData = slides[activeSlide];

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 4500);

    return () => window.clearInterval(interval);
  }, [slides.length]);

  const proofItems = t('hero.proofItems', { returnObjects: true }) as string[];

  return (
    <HeroSection id="hero">
      <HeroGrid>
        <HeroContent>
          <HeroBadge>
            <FaCheckCircle />
            {t('hero.badge')}
          </HeroBadge>
          <HeroTitle>SheetMate</HeroTitle>
          <HeroDescription>
            {t('hero.description')}
          </HeroDescription>
          <ButtonGroup>
            <PrimaryButton
              href={STORE_URL}
              onClick={() => trackLandingEvent('store_cta_click', { placement: 'hero_primary' })}
            >
              {t('hero.primaryButton')}
              <FaArrowRight />
            </PrimaryButton>
            <SecondaryLink
              href="#features"
              onClick={() => trackLandingEvent('section_cta_click', { placement: 'hero_secondary', target: 'features' })}
            >
              {t('hero.secondaryButton')}
            </SecondaryLink>
          </ButtonGroup>
          <ProofList>
            {proofItems.map((item, index) => (
              <ProofItem key={item}>
                {index === 0 && <FaCode />}
                {index === 1 && <FaColumns />}
                {index === 2 && <FaLock />}
                <span>{item}</span>
              </ProofItem>
            ))}
          </ProofList>
        </HeroContent>

        <ProductFrame aria-label={t('hero.productFrameLabel')}>
          <ProductToolbar>
            <ToolbarTitle><span /> SheetMate</ToolbarTitle>
            <ToolbarControls aria-hidden="true">
              <button />
              <button />
              <button />
            </ToolbarControls>
          </ProductToolbar>
          <PreviewLayout>
            <SheetMock>
              <SheetGrid>
                <div className="head" />
                <div className="head">A</div>
                <div className="head">B</div>
                <div className="head">C</div>
                <div className="head">1</div>
                <div>{t('hero.sheetHeaders.case')}</div>
                <div>{activeSlideData.column}</div>
                <div>{t('hero.sheetHeaders.renderAs')}</div>
                <div className="head">2</div>
                <div>{activeSlideData.rowLabel}</div>
                <div className="active">{activeSlideData.cell}</div>
                <div>{activeSlideData.note}</div>
                <div className="head">3</div>
                <div>{t('hero.sheetHeaders.rawCell')}</div>
                <div>{t('hero.sheetHeaders.copyable')}</div>
                <div>{t('hero.sheetHeaders.autoDetect')}</div>
              </SheetGrid>
            </SheetMock>
            <PreviewPanel>
              <FormatTabs>
                {slides.map((slide, index) => (
                  <FormatTab
                    key={slide.label}
                    $active={index === activeSlide}
                    onClick={() => {
                      setActiveSlide(index)
                      trackLandingEvent('hero_demo_tab_click', { content_type: slide.id })
                    }}
                  >
                    {slide.label}
                  </FormatTab>
                ))}
              </FormatTabs>
              <RenderedPreview aria-label={activeSlideData.label}>
                {renderPreview(activeSlideData.id, t)}
              </RenderedPreview>
            </PreviewPanel>
          </PreviewLayout>
        </ProductFrame>
      </HeroGrid>
      <BelowFoldCue>
        {t('hero.belowFoldCue')}
      </BelowFoldCue>
    </HeroSection>
  );
};

export default Hero;
